import { 
  db 
} from '../firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  limit, 
  query, 
  where 
} from 'firebase/firestore';
import { THAI_ADDRESSES_DATABASE, ThaiAddress } from '../data/thaiAddresses';

const ADDRESS_COLLECTION = 'thai_addresses';

/**
 * Seeds the Thailand address database to Firestore on first load.
 * Run asynchronously in the background.
 */
export async function seedAddressesToCloud(): Promise<void> {
  try {
    const q = query(collection(db, ADDRESS_COLLECTION), limit(1));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      // Already seeded
      return;
    }

    console.log('Seeding Thai address database to Firestore internal cloud storage...');
    // Seed in batches or chunk sets
    const promises = THAI_ADDRESSES_DATABASE.map(async (addr, index) => {
      const docId = `addr_${index}`;
      const docRef = doc(db, ADDRESS_COLLECTION, docId);
      await setDoc(docRef, addr);
    });
    
    // Execute all in parallel
    await Promise.all(promises);
    console.log('Seeding completed successfully!');
  } catch (err) {
    console.info('Address database cloud seeding skipped or restricted by permissions (using high-speed local search fallback).');
  }
}

/**
 * Searches the address database. It prioritizes Firestore cloud query, 
 * but has instant local fallback to guarantee sub-millisecond, latency-free suggestions.
 */
export async function searchThaiAddress(searchText: string): Promise<ThaiAddress[]> {
  const cleanSearch = searchText.trim().toLowerCase();
  if (!cleanSearch) return [];

  // If search is numeric (zipcode)
  const isNumeric = /^\d+$/.test(cleanSearch);

  try {
    // Try to query Firestore cloud storage first
    let q;
    if (isNumeric) {
      q = query(
        collection(db, ADDRESS_COLLECTION), 
        where('z', '>=', cleanSearch),
        where('z', '<=', cleanSearch + '\uf8ff'),
        limit(15)
      );
    } else {
      q = query(
        collection(db, ADDRESS_COLLECTION), 
        where('s', '>=', cleanSearch),
        where('s', '<=', cleanSearch + '\uf8ff'),
        limit(15)
      );
    }

    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const results: ThaiAddress[] = [];
      snapshot.forEach(doc => {
        results.push(doc.data() as ThaiAddress);
      });
      return results;
    }
  } catch (err) {
    console.warn('Firestore query failed/throttled. Using high-speed local fallback database.', err);
  }

  // Local search fallback (instantaceous search)
  return THAI_ADDRESSES_DATABASE.filter(item => {
    if (isNumeric) {
      return item.z.startsWith(cleanSearch);
    } else {
      return (
        item.s.toLowerCase().includes(cleanSearch) ||
        item.d.toLowerCase().includes(cleanSearch) ||
        item.p.toLowerCase().includes(cleanSearch)
      );
    }
  }).slice(0, 15);
}

/**
 * Parses a combined Thai address string into 3 structured components:
 * addr1: House/building/road/soi
 * addr2: Sub-district / District / Province
 * addr3: 5-digit postal code
 */
export function parseThaiAddress(addressStr: string): { addr1: string; addr2: string; addr3: string } {
  let addr1 = '';
  let addr2 = '';
  let addr3 = '';
  if (!addressStr) return { addr1, addr2, addr3 };

  const trimmed = addressStr.trim();
  const postalMatch = trimmed.match(/\b\d{5}\b$/);
  if (postalMatch) {
    addr3 = postalMatch[0];
    const withoutPostal = trimmed.substring(0, postalMatch.index).trim();
    const parts = withoutPostal.split(/\s+/);
    if (parts.length >= 3) {
      addr1 = parts.slice(0, Math.max(1, parts.length - 2)).join(' ');
      addr2 = parts.slice(Math.max(1, parts.length - 2)).join(' ');
    } else if (parts.length === 2) {
      addr1 = parts[0];
      addr2 = parts[1];
    } else {
      addr1 = withoutPostal;
    }
  } else {
    addr1 = trimmed;
  }
  return { addr1, addr2, addr3 };
}
